import { useAuth } from '../../hooks/useAuth';
import { useSupportSession } from '../../hooks/useSupportSession';
import { useMySupportGrants } from '../../api/hooks';
import { ROLE_LABELS } from '../team/roleLabels';
import { darkTheme, FONT } from '../../theme/tokens';
import type { SupportGrant } from '../../types';
import { formatRemaining, useNow } from './supportTime';

const t = darkTheme;

/**
 * The staff org switcher's list (SC-2948): the signed-in staff user's LIVE
 * support grants, each with an Enter action that re-scopes the session into that
 * client org. Renders nothing for non-staff — the list endpoint is staff-only
 * and the switcher must never appear on the client side.
 *
 * Shown in two places: the sidebar account menu (`variant="menu"`, where a click
 * also closes the popover) and the staff landing at /admin (`variant="card"`),
 * because in the platform scope the app shell can't load any org data — /admin
 * is where staff actually start.
 */
export function SupportGrantList({ variant }: { variant: 'menu' | 'card' }) {
  const { isEkomAdmin } = useAuth();
  // Gate before any query hook mounts: non-staff must never call the endpoint.
  if (!isEkomAdmin) return null;
  return <StaffGrantList variant={variant} />;
}

function StaffGrantList({ variant }: { variant: 'menu' | 'card' }) {
  const { user } = useAuth();
  const { support, enterSupport } = useSupportSession();
  const { data, isPending, isError, refetch } = useMySupportGrants(user?.id, true);
  const now = useNow();

  // A grant can pass its expiry while the list sits on screen; expiry is
  // enforced per request, so it must stop looking enterable right then.
  const grants = (data?.grants ?? []).filter((g) => Date.parse(g.expires_at) > now);

  let body: React.ReactNode;
  if (isPending) {
    body = <Hint>Loading support grants…</Hint>;
  } else if (isError) {
    // A failed read is usually transient (network, deploy), so a retry can help.
    body = (
      <Hint tone="error">
        Couldn&apos;t load your support grants.{' '}
        <button
          type="button"
          onClick={(e) => {
            // In the account menu, keep the popover open for the retry.
            e.stopPropagation();
            void refetch();
          }}
          style={{
            background: 'none', border: 'none', padding: 0, font: 'inherit',
            color: t.text, textDecoration: 'underline', cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </Hint>
    );
  } else if (grants.length === 0) {
    body = <Hint>No live support grants.</Hint>;
  } else {
    body = grants.map((g) => (
      <GrantRow
        key={g.membership_id}
        grant={g}
        now={now}
        current={support?.organizationId === g.organization.id}
        variant={variant}
        onEnter={() => { void enterSupport(g); }}
      />
    ));
  }

  return (
    <div role="group" aria-label="Support access" style={{ fontFamily: FONT }}>
      <div
        aria-hidden
        style={{
          fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', color: t.text3,
          padding: variant === 'menu' ? '8px 12px 4px' : '0 0 8px',
        }}
      >
        SUPPORT ACCESS
      </div>
      {body}
      {data?.truncated && (
        <Hint>Some grants may be missing: there are more staff grants than fit on one page.</Hint>
      )}
    </div>
  );
}

function GrantRow({
  grant,
  now,
  current,
  variant,
  onEnter,
}: {
  grant: SupportGrant;
  now: number;
  current: boolean;
  variant: 'menu' | 'card';
  onEnter: () => void;
}) {
  const name = grant.organization.name;
  const meta = `${ROLE_LABELS[grant.role] ?? grant.role} · ${formatRemaining(grant.expires_at, now)} left`;
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: variant === 'menu' ? '6px 12px' : '10px 12px',
        borderRadius: 6,
        ...(variant === 'card' ? { border: `1px solid ${t.border}`, marginBottom: 6 } : {}),
      }}
    >
      <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
        <div style={{ fontSize: 13, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </div>
        <div style={{ fontSize: 11, color: t.text3 }}>{meta}</div>
      </div>
      {current ? (
        <span style={{ fontSize: 11, color: t.amberText }}>You&apos;re here</span>
      ) : (
        <button
          type="button"
          onClick={onEnter}
          aria-label={`Enter ${name}`}
          style={{
            padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
            fontFamily: FONT, cursor: 'pointer',
            background: t.amberBg, color: t.amberText, border: `1px solid ${t.amber}`,
            flexShrink: 0,
          }}
        >
          Enter
        </button>
      )}
    </div>
  );
}

function Hint({ children, tone }: { children: React.ReactNode; tone?: 'error' }) {
  return (
    <div style={{ padding: '4px 12px 8px', fontSize: 12, color: tone === 'error' ? t.redText : t.text3 }}>
      {children}
    </div>
  );
}
